import { useTranslation } from '../../lib/i18n';
import { continueOnChannelHref, type ExternalChannel } from '../../lib/external-channel';

// The read-only composer for an externally-owned conversation: the same white
// rounded card as ComposerPill, but locked — a line saying where the
// conversation lives plus a "Continue …" CTA that deep-links into the owning
// app or agent platform. Messages still render above; only sending moves over.
//
// EXTERNAL_AGENT differs from WhatsApp/SMS in one way that shapes this component: its
// destination is PER-CONVERSATION (each agent has its own link) rather than one
// constant number, and the platform's NAME is data too. Both arrive via
// `agentPlatform`, read from the conversation's metadata — so Symphony renders
// "Continue in Gemini Enterprise" without any code that knows Gemini exists.
//
// GROUP seam: `isWaGroup` marks a WhatsApp GROUP conversation. Once group
// metadata ships (invite link on the conversation), a group's CTA should open
// the group via its invite link instead of the 1:1 wa.me number — branch here
// (and in continueOnChannelHref). Until then groups get the same WhatsApp CTA,
// without prefilled text.
export function ChannelLockedBar({
  channel,
  agentPlatform,
  compact,
}: {
  channel: ExternalChannel;
  // EXTERNAL_AGENT only: the external agent platform's display name and return link, resolved from the
  // conversation (see readAgentPlatform, which drops an unsafe URL). Absent for WhatsApp/SMS, and
  // absent for an agent conversation whose link did not pass validation — in which case the bar still
  // explains why the conversation is read-only but offers no button, because a CTA with nowhere to go
  // is worse than none.
  agentPlatform?: { name: string; url?: string } | null;
  // Reserved for the group-CTA branch above — read it once the invite link
  // exists on group conversations.
  isWaGroup?: boolean;
  // The small floating panel — tighter padding, same content.
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const href = continueOnChannelHref(channel, agentPlatform?.url);
  const message =
    channel === 'WHATSAPP'
      ? t('chat.channelLocked.whatsapp.message')
      : channel === 'SMS'
        ? t('chat.channelLocked.sms.message')
        : agentPlatform
          ? t('chat.channelLocked.externalAgent.message', { platform: agentPlatform.name })
          : t('chat.channelLocked.externalAgent.messageUnknown');
  const cta =
    channel === 'WHATSAPP'
      ? t('chat.channelLocked.whatsapp.cta')
      : channel === 'SMS'
        ? t('chat.channelLocked.sms.cta')
        : t('chat.channelLocked.externalAgent.cta', { platform: agentPlatform?.name ?? '' });
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        alignSelf: 'stretch',
        borderRadius: 20,
        background: 'var(--ds-surface-sunken)',
        padding: compact ? '12px 14px' : '14px 20px',
        boxShadow: '0 -4px 18px 0 rgba(var(--ds-black-rgb), 0.1)',
      }}
    >
      {/* Padlock — the "read-only" glyph, quiet ink like the composer icons. */}
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden style={{ flexShrink: 0, color: 'rgba(var(--ink-rgb),0.45)' }}>
        <rect x="3.75" y="8" width="10.5" height="7" rx="2" stroke="currentColor" strokeWidth="1.4" />
        <path d="M6 8V5.75a3 3 0 0 1 6 0V8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
      <span style={{ flex: 1, minWidth: 140, fontSize: 13.5, fontWeight: 'var(--sym-weight-medium)', lineHeight: 1.4, color: 'rgba(var(--ink-rgb),0.7)' }}>
        {message}
      </span>
      {href && (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          flexShrink: 0,
          padding: '8px 16px',
          borderRadius: 'var(--sym-radius-button)',
          background: 'var(--accent-solid)',
          color: 'rgba(var(--ink-rgb), 0.9)',
          fontSize: 13,
          fontWeight: 'var(--sym-weight-semibold)',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        {cta}
      </a>
      )}
    </div>
  );
}
